import styled from "@emotion/styled";
import React from "react";

import { ContainerProps } from "@brexhq/next/src/components/layout/Container/Container";
import { BynderAsset } from "@brexhq/next/src/components/shared/BynderAsset/BynderAsset";
import { ImageBorderRadiusWrapper } from "@brexhq/next/src/components/shared/BynderAsset/Image/BynderImage";
import { ComponentPerBreakpoint } from "@brexhq/next/src/components/shared/ComponentPerBreakpoint";
import { SanityMediaBlockProps } from "@brexhq/shared/types/modules/BlockRowsModule/BlockRowItems";

import { buildLinkPath } from "../../pageBuilder/utils";
import { Link } from "../../shared/Link";

const Media = styled(BynderAsset)<{
  objectFit?: React.CSSProperties["objectFit"];
  objectPosition?: React.CSSProperties["objectPosition"];
  mobileMaxWidth?: React.CSSProperties["maxWidth"];
  mobileHeight?: React.CSSProperties["height"];
  desktopMaxWidth?: React.CSSProperties["maxWidth"];
  desktopHeight?: React.CSSProperties["height"];
}>(
  ({
    theme: { breakpoints },
    objectFit,
    objectPosition,
    mobileMaxWidth,
    mobileHeight,
    desktopMaxWidth,
    desktopHeight,
  }) => ({
    objectFit,
    objectPosition,
    height: desktopHeight,
    width: "100%",
    [breakpoints.LRG]: {
      maxWidth: desktopMaxWidth ?? "100%",
    },
    [breakpoints.MED]: {
      maxWidth: desktopMaxWidth ?? "100%",
    },
    [breakpoints.SML]: {
      height: mobileHeight,
      maxWidth: mobileMaxWidth ?? "100%",
    },
  }),
);

/**
 * Lives in Block Module.
 * Shows image or video in a contain or cover style.
 */

type Props = Omit<SanityMediaBlockProps, "_key" | "_type"> & {
  animationTargets?: Array<"move-right" | "scale">;
};

export const MediaBlock = ({
  marginTop,
  desktopMedia,
  mobileMedia,
  imageStyle,
  verticalAnchorPoint,
  horizontalAnchorPoint,
  setImageHeight,
  desktopImageHeight,
  mobileImageHeight,
  setImageMaxWidth,
  desktopImageMaxWidth,
  mobileImageMaxWidth,
  link,
  animationTargets,
  programName,
}: Props) => {
  const styleProps = {
    objectFit: imageStyle,
    objectPosition: `${verticalAnchorPoint} ${horizontalAnchorPoint}`,
    mobileHeight: setImageHeight ? mobileImageHeight : undefined,
    desktopHeight: setImageHeight ? desktopImageHeight : undefined,
    mobileMaxWidth: setImageMaxWidth ? mobileImageMaxWidth : "100%",
    desktopMaxWidth: setImageMaxWidth ? desktopImageMaxWidth : "100%",
  };

  const containerProps: ContainerProps = {
    margin: [marginTop, 0, 0, 0],
    width: "100%",
    maxWidth: {
      LRG: setImageMaxWidth ? desktopImageMaxWidth : "100%",
      MED: setImageMaxWidth ? desktopImageMaxWidth : "100%",
      SML: setImageMaxWidth ? mobileImageMaxWidth : "100%",
    },
  };

  const linkTo = link ? buildLinkPath({ link }) : undefined;

  const animAttr = (animationTargets || []).join(" ");
  const mediaContents = (
    <ComponentPerBreakpoint
      LRG={
        <ImageBorderRadiusWrapper mediaStyle={desktopMedia.mediaStyle}>
          <Media
            loopVideo={!desktopMedia.disableVideoLoop}
            showVideoControls={desktopMedia.showVideoControls}
            asset={desktopMedia.media}
            thumbnail={desktopMedia.thumbnail}
            play={desktopMedia.play}
            {...styleProps}
            programName={programName}
          />
        </ImageBorderRadiusWrapper>
      }
      SML={
        <ImageBorderRadiusWrapper mediaStyle={mobileMedia.mediaStyle}>
          <Media
            loopVideo={!mobileMedia.disableVideoLoop}
            showVideoControls={mobileMedia.showVideoControls}
            asset={mobileMedia.media}
            thumbnail={desktopMedia.thumbnail}
            play={mobileMedia.play}
            {...styleProps}
            programName={programName}
          />
        </ImageBorderRadiusWrapper>
      }
      additionalContainerProps={containerProps}
    />
  );

  const media = <div data-anim={animAttr || undefined}>{mediaContents}</div>;

  return linkTo ? (
    <Link
      to={linkTo}
      label={desktopMedia.media.description ?? desktopMedia.media.name ?? ""}
      component="media-block-link"
      modifier="unstyled"
    >
      {media}
    </Link>
  ) : (
    media
  );
};
